import { advancedSearchPathFragment, executionsPathFragment } from "utils/RouterManager/constants";
import {
    FILTER_QUERY_KEYS,
    formatSearchParams,
    parseSearchParams,
} from "utils/RouterManager/searchParams";
import { getLocalStorageItem, getRelativeUrl, getWorkspaceUrl, parseJSONSafely, setLocalStorageItem } from "utils/service";

export type FilterPageContext =
    | { page: "execution-filter"; recordType: string; recordId: string }
    | { page: "log-filter"; recordType: string; recordId: string }
    | { page: "role-identity"; roleId: string }
    | { page: "advanced-search" };

export const advancedSearchFilterStorageKey = "filter:advanced-search";

export interface StoredFilterState {
    filter?: string;
    customFilter?: string;
    include_deleted?: boolean;
    hide_deleted?: boolean;
    tab?: string;
    /**
     * Every record type was selected when the filter was saved. On read, the saved
     * `resource_type` list is replaced by the current full list, so a record type added
     * after the filter was saved (e.g. EGRESS_POLICY) is selected too.
     */
    all_resource_types?: boolean;
}

export const getFilterStorageKey = (context: FilterPageContext): string => {
    switch (context.page) {
        case "execution-filter":
            return `filter:execution:${context.recordType}:${context.recordId}`;
        case "log-filter":
            return `filter:log:${context.recordType}:${context.recordId}`;
        case "role-identity":
            return `filter:role-identity:${context.roleId}`;
        case "advanced-search":
            return advancedSearchFilterStorageKey;
    }
};

export const setAdvancedSearchTab = (tab: string): void => {
    const current = readStoredFilter(advancedSearchFilterStorageKey) ?? {};
    // Switching to a different tab must NOT carry over a filter/customFilter that was
    // committed on another tab: those leaves are keyed to that tab's record type
    // (e.g. a custom-object `value.assignee` filter) and are invalid — a 400 "bad
    // request" — against a different tab's list, yielding a blank result. Mirror
    // AdvancedSearch.onTabChange, which resets to the target tab's initial filter.
    // The deleted-visibility toggles are preserved.
    const next: StoredFilterState = { tab };
    if (current.include_deleted !== undefined) {
        next.include_deleted = current.include_deleted;
    }
    if (current.hide_deleted !== undefined) {
        next.hide_deleted = current.hide_deleted;
    }
    writeStoredFilter(advancedSearchFilterStorageKey, next);
};

export const readStoredFilter = (key: string): StoredFilterState | null =>
    parseJSONSafely(getLocalStorageItem(key), null);

export const writeStoredFilter = (key: string, state: StoredFilterState): void => {
    setLocalStorageItem(key, state);
};

export const hasFilterQueryParams = (params: { [key: string]: string }): boolean =>
    FILTER_QUERY_KEYS.some((key) => key in params);

/** Build stored filter state from URL query params (values are already decoded). */
export const storedFilterFromQueryParams = (
    urlParams: { [key: string]: string }
): StoredFilterState => ({
    ...(urlParams.filter !== undefined && { filter: urlParams.filter }),
    ...(urlParams.customFilter !== undefined && { customFilter: urlParams.customFilter }),
    include_deleted:
        urlParams.include_deleted === undefined
            ? false
            : urlParams.include_deleted === "true",
    hide_deleted:
        urlParams.hide_deleted === undefined ? true : urlParams.hide_deleted === "true",
    ...(urlParams.tab !== undefined && { tab: urlParams.tab }),
});

/**
 * Import filter query params from a deep link into localStorage.
 * URL filter params always win over any previously stored filter.
 */
export const importFilterQueryFromUrl = (
    storageKey: string,
    urlParams: { [key: string]: string }
): boolean => {
    if (!hasFilterQueryParams(urlParams)) {
        return false;
    }
    writeStoredFilter(storageKey, storedFilterFromQueryParams(urlParams));
    return true;
};

/** Clean execution-list sub-page URL (no filter query params), without touching storage. */
export const getExecutionFilterPath = (recordType: string, recordId: string): string => {
    if (recordType === "project" || recordType === "bundle") {
        return `${getRelativeUrl(recordType, recordId)}&${executionsPathFragment}`;
    }
    return `${getWorkspaceUrl()}?${executionsPathFragment}`;
};

/**
 * Persist execution-list filter state and return a clean sub-page URL (no filter query params).
 *
 * `filterState.filter` / `filterState.customFilter` must be unencoded JSON strings
 * (from `getFilterString`), because they are read back from localStorage with `JSON.parse`.
 */
export const buildExecutionFilterPath = (
    recordType: string,
    recordId: string,
    filterState: Pick<StoredFilterState, "filter" | "customFilter">
): string => {
    writeStoredFilter(getFilterStorageKey({ page: "execution-filter", recordType, recordId }), {
        include_deleted: false,
        hide_deleted: true,
        ...filterState,
    });
    return getExecutionFilterPath(recordType, recordId);
};

export const buildExecutionFilterPathForResource = (
    resourceData: { id?: string; resource_type?: string; workspace_id?: string },
    filterState: Pick<StoredFilterState, "filter" | "customFilter">
): string => {
    const recordType = resourceData?.resource_type?.toLowerCase();
    const recordId = resourceData?.id;
    if (recordId && (recordType === "project" || recordType === "bundle" || recordType === "workspace")) {
        return buildExecutionFilterPath(recordType, recordId, filterState);
    }
    if (resourceData?.workspace_id) {
        return buildExecutionFilterPath("workspace", resourceData.workspace_id, filterState);
    }
    return `${getWorkspaceUrl()}?${executionsPathFragment}`;
};

/** Shareable deep link: sub-page URL with filter query params (imported to localStorage on open). */
export const buildExecutionFilterDeepLink = (
    recordType: string,
    recordId: string,
    filterState: Pick<StoredFilterState, "filter" | "customFilter" | "include_deleted" | "hide_deleted">
): string => {
    const basePath = getExecutionFilterPath(recordType, recordId);
    const queryStart = basePath.indexOf("?");
    const pathname = queryStart >= 0 ? basePath.slice(0, queryStart) : basePath;
    const params = parseSearchParams(queryStart >= 0 ? basePath.slice(queryStart + 1) : "");
    if (filterState.filter) {
        params.filter = filterState.filter;
    }
    if (filterState.customFilter) {
        params.customFilter = filterState.customFilter;
    }
    if (filterState.include_deleted !== undefined) {
        params.include_deleted = String(filterState.include_deleted);
    }
    if (filterState.hide_deleted !== undefined) {
        params.hide_deleted = String(filterState.hide_deleted);
    }
    const search = formatSearchParams(params);
    return search ? `${pathname}?${search}` : pathname;
};

/**
 * Shareable deep link for Advanced Search: an absolute URL carrying the given filter state
 * as query params (the current committed filter, from `readStoredFilter`). Opening it
 * re-imports the filter into localStorage (via `importFilterQueryFromUrl`, wired through
 * `useSetQueryString`) and the visible URL then reverts to the bare advanced-search route,
 * so the filter params are consumed once.
 *
 * `filterState.filter` / `filterState.customFilter` must be unencoded JSON strings
 * (as stored by `readStoredFilter`); `formatSearchParams` URL-encodes them.
 */
export const buildAdvancedSearchDeepLink = (filterState: StoredFilterState | null): string => {
    // The sub-page route flag must be present so RouterManager renders Advanced Search
    // (formatSearchParams emits it as a bare `advanced-search` flag).
    const params: Record<string, string> = { [advancedSearchPathFragment]: "" };
    if (filterState?.filter) {
        params.filter = filterState.filter;
    }
    if (filterState?.customFilter) {
        params.customFilter = filterState.customFilter;
    }
    if (filterState?.include_deleted !== undefined) {
        params.include_deleted = String(filterState.include_deleted);
    }
    if (filterState?.hide_deleted !== undefined) {
        params.hide_deleted = String(filterState.hide_deleted);
    }
    if (filterState?.tab) {
        params.tab = filterState.tab;
    }
    const search = formatSearchParams(params);
    const origin = typeof window !== "undefined" ? window.location.origin : "";
    return `${origin}${getWorkspaceUrl()}?${search}`;
};

/** True when `selected` contains every type of `allTypes`. */
export const coversAllResourceTypes = (selected: unknown, allTypes: string[]): boolean =>
    Array.isArray(selected) && allTypes.length > 0 && allTypes.every((type) => selected.includes(type));

/**
 * Replace the `resource_type` leaf of a stored filter string with `allTypes`.
 * Returns the filter unchanged when it has no `resource_type` leaf or does not parse.
 */
export const expandStoredResourceTypes = (filter: string, allTypes: string[]): string => {
    let parsed: { and?: Array<{ field?: string; value?: unknown }> };
    try {
        parsed = JSON.parse(filter);
    } catch {
        return filter;
    }
    if (!parsed || !Array.isArray(parsed.and)) {
        return filter;
    }
    let changed = false;
    const and = parsed.and.map((leaf) => {
        if (leaf?.field === "resource_type") {
            changed = true;
            return { ...leaf, value: [...allTypes] };
        }
        return leaf;
    });
    return changed ? JSON.stringify({ ...parsed, and }) : filter;
};

/**
 * Merge URL query params (record id, sub-page flags) with filter state from localStorage.
 * With `allResourceTypes`, a filter saved as "all types" is expanded to that list.
 */
export const mergeQueryParamsWithStoredFilter = (
    queryParams: { [key: string]: string },
    stored: StoredFilterState | null,
    allResourceTypes?: string[]
): { [key: string]: string } => {
    if (!stored) {
        return queryParams;
    }
    const merged = { ...queryParams };
    if (stored.filter) {
        merged.filter =
            stored.all_resource_types && allResourceTypes
                ? expandStoredResourceTypes(stored.filter, allResourceTypes)
                : stored.filter;
    }
    if (stored.customFilter) {
        merged.customFilter = stored.customFilter;
    }
    if (stored.include_deleted !== undefined) {
        merged.include_deleted = String(stored.include_deleted);
    }
    if (stored.hide_deleted !== undefined) {
        merged.hide_deleted = String(stored.hide_deleted);
    }
    if (stored.tab) {
        merged.tab = stored.tab;
    }
    return merged;
};
